import {
	Transform,
	TransformPosition,
	TransformRotation,
	BoxDimensions,
} from '@ikea-aoa/elements-3d';
import { ConnectableObject, ConnectionManager } from '../connection-manager/ConnectionManager';
import { TransformVector } from '../utils/TransformVector';
import { GridBox } from './GridBox';
import { SceneObject } from '..';

export interface ThresholdDimensions {
	width: number;
	depth: number;
}

interface OffsetModifier {
	positive: TransformPosition;
	negative: TransformPosition;
}

export class GridTools {
	constructor(
		private sceneObjects: Map<string, ConnectableObject>,
		private connectionManager: ConnectionManager
	) {}

	/**
	 * Creates a map of grid boxes. Boxes can be used for intersection check.
	 */
	public createGridBoxes(
		objects = this.sceneObjects,
		threshold?: number | ThresholdDimensions,
		ignore?: string[]
	): Map<string, GridBox> {
		const grid = new Map<string, GridBox>();

		objects.forEach((o) => {
			// Legs are always ignored
			if (o.articleId == 'leg') {
				return;
			}
			if (ignore && ignore.includes(o.id)) {
				return;
			}
			const bounds = this.getBoundsName(o.articleId);
			const gridBox = this.createGridBoxFromObject(o, bounds, threshold);
			grid.set(o.id, gridBox);
		});

		return grid;
	}

	/**
	 * Get a grid box from articleId and world transform.
	 */
	public createGridBox(
		articleId: string,
		worldTransform: Transform,
		bounds?: string,
		threshold?: number | ThresholdDimensions
	): GridBox {
		const boundsName = bounds ? bounds : this.getBoundsName(articleId);
		const { position, rotation } = worldTransform;
		// const boundsPosition = this.getArticleBoundsPosition(articleId, position, rotation, boundsName);
		const boundsPosition = this.getBoundsPosition(articleId, boundsName);
		const size = this.getArticleSize(articleId, boundsName);

		return new GridBox(size, position, rotation, boundsPosition, threshold);
	}

	/**
	 * Get a grid box from object.
	 */
	public createGridBoxFromObject(
		objects: SceneObject,
		bounds?: string,
		threshold?: number | ThresholdDimensions
	): GridBox {
		const transform = this.connectionManager.getWorldTransform(objects);
		const boundsName = bounds ? bounds : this.getBoundsName(objects.articleId);
		return this.createGridBox(objects.articleId, transform, boundsName, threshold);
	}

	/**
	 * Check Intersection between two articleIds.
	 */
	public checkIntersectionBetween(
		a: { articleId: string; worldTransform: Transform; bounds?: string },
		b: { articleId: string; worldTransform: Transform; bounds?: string },
		threshold?: number | ThresholdDimensions
	): boolean {
		const boxA = this.createGridBox(
			a.articleId,
			a.worldTransform,
			a.bounds ? a.bounds : 'default',
			threshold
		);
		const boxB = this.createGridBox(
			b.articleId,
			b.worldTransform,
			b.bounds ? b.bounds : 'default',
			threshold
		);

		return this.intersects(boxA, boxB);
	}

	/**
	 * Check Intersection between two gridboxes.
	 */
	public intersects(boxA: GridBox, boxB: GridBox): boolean {
		const a = boxA.getBox();
		const b = boxB.getBox();
		return !(b.left > a.right || b.right < a.left || b.top < a.bottom || b.bottom > a.top);
	}

	/**
	 * Check intersections for article.
	 */
	public checkIntersection(
		articleId: string,
		worldTransform: Transform,
		bounds?,
		threshold?: number | ThresholdDimensions,
		gridBoxes?: Map<string, GridBox>,
		ignore?: string[]
	): Array<string>;

	/**
	 * Check intersections for object.
	 */
	public checkIntersection(
		object: ConnectableObject,
		worldTransform?: Transform,
		bounds?,
		threshold?: number | ThresholdDimensions,
		gridBoxes?: Map<string, GridBox>,
		ignore?: string[],
		includeChildOnly?: boolean
	): Array<string>;

	public checkIntersection(
		item: string | ConnectableObject,
		worldTransform?: Transform,
		bounds = 'default',
		threshold?: number | ThresholdDimensions,
		gridBoxes?: Map<string, GridBox>,
		ignore?: string[],
		includeChildOnly = true
	): Array<string> {
		const boxes = gridBoxes ? gridBoxes : this.createGridBoxes(this.sceneObjects);

		// article overload
		if (typeof item == 'string') {
			const box = this.createGridBox(item, worldTransform as Transform, bounds, threshold);
			return this.checkBoxIntersections(box, boxes, ignore);
		}

		// object overload
		const transform = worldTransform
			? worldTransform
			: this.connectionManager.getWorldTransform(item);
		const box = this.createGridBox(item.articleId, transform, bounds, threshold);
		const children = this.createChildrenGridBoxes(item.id, transform, threshold, includeChildOnly);
		const allBoxes = [box, ...Array.from(children.values())];
		return this.checkMultyBoxesIntersections(allBoxes, boxes, ignore);
	}

	/**
	 * Check intersections for grid box.
	 */
	public checkBoxIntersections(
		gridBox: GridBox,
		gridBoxes?: Map<string, GridBox>,
		ignore?: string[]
	): Array<string> {
		const intersections = new Array<string>();
		const boxes = gridBoxes ? gridBoxes : this.createGridBoxes(this.sceneObjects);
		boxes.forEach((box, key) => {
			if (ignore && ignore.includes(key)) {
				return;
			}
			if (this.intersects(gridBox, box)) {
				intersections.push(key);
			}
		});
		return intersections;
	}

	/**
	 * Check intersections for multiple grid boxes.
	 */
	public checkMultyBoxesIntersections(
		gridBoxesA?: Array<GridBox> | Map<string, GridBox>,
		gridBoxesB?: Map<string, GridBox>,
		ignore?: string[]
	): Array<string> {
		const intersections = new Array<string>();
		const boxes = gridBoxesB ? gridBoxesB : this.createGridBoxes(this.sceneObjects);
		boxes.forEach((gridBox, key) => {
			if (ignore && ignore.includes(key)) {
				return;
			}
			(gridBoxesA as GridBox[]).forEach((box) => {
				if (this.intersects(box, gridBox)) {
					intersections.push(key);
				}
			});
		});
		return intersections;
	}

	/**
	 * Get all snapping points of object to passed grid. Without intersection check.
	 */
	public getAllSnappingPositions(
		articleId: string,
		stepSize?: number,
		stepsCount?,
		itemIds?: string[]
	): Map<string, TransformPosition[][]>;

	public getAllSnappingPositions(
		sceneObject: ConnectableObject,
		stepSize?: number,
		stepsCount?,
		itemIds?: string[]
	): Map<string, TransformPosition[][]>;

	public getAllSnappingPositions(
		item: ConnectableObject | string,
		stepSize = 4,
		stepsCount = 3,
		itemIds = Array.from(this.sceneObjects.keys())
	): Map<string, TransformPosition[][]> {
		const points = new Map<string, TransformPosition[][]>();

		// validating overload
		const size = typeof item == 'string' ? this.getArticleSize(item) : this.getObjectSize(item);
		const rotation =
			typeof item == 'string'
				? TransformVector.zero()
				: this.connectionManager.getWorldRotation(item);
		const children = typeof item == 'string' ? [] : this.connectionManager.getAllChildren(item.id);

		const childrenOffset = this.getChildrenOffset(size, rotation, children);

		itemIds.forEach((id) => {
			// always ignore item itself and it's children
			if ((typeof item == 'object' && item.id == id) || children.includes(id)) {
				return;
			}

			// always ignore child only objects
			const object = this.sceneObjects.get(id);
			if (object) {
				const addAsChild = this.connectionManager.isAddAsChild(object.articleId);
				const isSeparate = this.connectionManager.isSeparate(object.articleId);
				if (addAsChild && !isSeparate) {
					return;
				}
				// create snapping points
				points.set(
					id,
					this.calculateSnappingPositions(object, size, stepSize, stepsCount, childrenOffset)
				);
			}
		});
		return points;
	}

	private calculateSnappingPositions(
		initiator: ConnectableObject, // object that we are connecting to
		relativeSize: BoxDimensions, // already rotated size of dragged object
		stepSize: number,
		stepsCount: number,
		childrenOffset: OffsetModifier // dragged object childrens offset
	): TransformPosition[][] {
		const initiatorPosition = this.getObjectBoundsPosition(initiator); // basicly center of object in world coordinates.
		const initiatorSize = this.getObjectSize(initiator); // rotated size of object that we are connecting to
		const initiatorRotation = this.connectionManager.getWorldRotation(initiator);

		const points = new Array<Array<TransformPosition>>();
		const xOffset = initiatorSize.width / 2 + relativeSize.width / 2;
		const zOffset = initiatorSize.depth / 2 + relativeSize.depth / 2;

		const initiatorCenterZ = TransformVector.clone(initiatorPosition);
		const initiatorCenterX = TransformVector.clone(initiatorPosition);

		// getting clean rotation and avaid rounding errors.
		const normalizedRotation = Math.round((initiatorRotation.y as number) * (180 / Math.PI)) % 360;

		if (initiatorCenterX && initiatorCenterZ) {
			// modify center positions depentding on rotation
			if (normalizedRotation == 0) {
				(initiatorCenterX.z as number) -= initiatorSize.depth / 2 - relativeSize.depth / 2;
			}
			if (normalizedRotation == 90 || normalizedRotation == -270) {
				(initiatorCenterZ.x as number) -= initiatorSize.width / 2 - relativeSize.width / 2;
			}
			if (normalizedRotation == 180 || normalizedRotation == -180) {
				(initiatorCenterX.z as number) += initiatorSize.depth / 2 - relativeSize.depth / 2;
			}
			if (normalizedRotation == 270 || normalizedRotation == -90) {
				(initiatorCenterZ.x as number) += initiatorSize.width / 2 - relativeSize.width / 2;
			}
		}

		this.createAxisPoints('x', xOffset, stepSize, childrenOffset, stepsCount).forEach((p) => {
			const wp = p.map((p) => TransformVector.add(p, initiatorCenterX));
			points.push(wp);
		});
		this.createAxisPoints('z', zOffset, stepSize, childrenOffset, stepsCount).forEach((p) => {
			const wp = p.map((p) => TransformVector.add(p, initiatorCenterZ));
			points.push(wp);
		});
		return points;
	}

	private getObjectSize(sceneObject: ConnectableObject, bounds = 'default'): BoxDimensions {
		const d = this.getArticleSize(sceneObject.articleId, bounds);
		const rotation = this.connectionManager.getWorldRotation(sceneObject);
		return this.rotateDimensions(d, rotation);
	}

	private getArticleSize(articleId: string, bounds = 'default'): BoxDimensions {
		const model = this.connectionManager.getModelData(articleId);
		if (model) {
			return model.bounds[bounds].size;
		} else {
			throw new Error(`Not able to get ModelData`);
		}
	}

	private getChildrenOffset(
		size: BoxDimensions,
		rotation: TransformRotation,
		children: Array<string>
	): OffsetModifier {
		const offset = new Object() as OffsetModifier;
		offset.positive = TransformVector.zero();
		offset.negative = TransformVector.zero();

		children.forEach((child) => {
			const childObject = this.sceneObjects.get(child);
			if (childObject && childObject.transform.position) {
				if (
					this.connectionManager.isAddAsChild(childObject.articleId) &&
					!this.connectionManager.isSeparate(childObject.articleId)
				) {
					return;
				}
				const position = TransformVector.rotate(childObject.transform.position, rotation);
				const childSize = this.getObjectSize(childObject);

				const x = Math.abs(position.x as number) - size.width / 2 + childSize.width / 2;
				const z = Math.abs(position.z as number) - size.depth / 2 + childSize.depth / 2;
				const offsetX = (position.x as number) < 0 ? 'positive' : 'negative';
				const offsetZ = (position.z as number) < 0 ? 'positive' : 'negative';
				offset[offsetX as string].x =
					offset[offsetX as string].x < x ? x : offset[offsetX as string].x;
				offset[offsetZ as string].z =
					offset[offsetX as string].z < z ? z : offset[offsetX as string].z;
			}
		});
		return offset;
	}

	private getObjectBoundsPosition(
		sceneObject: ConnectableObject,
		bounds = 'default'
	): TransformPosition {
		const purePosition = this.connectionManager.getWorldPosition(sceneObject);
		const rotation = this.connectionManager.getWorldRotation(sceneObject);

		const model = this.connectionManager.getModelData(sceneObject.articleId);
		if (!model) {
			throw new Error('Model is not found!');
		}

		const posOffset = TransformVector.rotate(
			model.bounds[bounds].position || TransformVector.zero(),
			rotation
		);
		return TransformVector.add(purePosition, posOffset);
	}

	private rotateDimensions(d: BoxDimensions, rotation: TransformRotation): BoxDimensions {
		if (rotation.x == 0 && rotation.y == 0 && rotation.z == 0) {
			return d;
		}
		let vector = {
			x: d.width,
			y: d.height,
			z: d.depth,
		} as TransformPosition;
		vector = TransformVector.rotate(vector, rotation);

		return {
			width: Math.abs(vector.x as number),
			height: Math.abs(vector.y as number),
			depth: Math.abs(vector.z as number),
		} as BoxDimensions;
	}

	private createAxisPoints(
		axis: 'x' | 'y' | 'z',
		offset: number,
		step: number,
		offsetModifier: OffsetModifier,
		amount: number
	): Array<Array<TransformPosition>> {
		const positiveAxis = new Array<TransformPosition>();
		const negativeAxis = new Array<TransformPosition>();
		let nextStep = offset + step;
		for (let i = 0; i < amount; i++) {
			const positive = TransformVector.zero();
			const negative = TransformVector.zero();
			positive[axis as string] +=
				nextStep + (offsetModifier ? offsetModifier.positive[axis as string] : 0);
			negative[axis as string] -=
				nextStep + (offsetModifier ? offsetModifier.negative[axis as string] : 0);
			positiveAxis.push(positive);
			negativeAxis.push(negative);
			nextStep += step;
		}
		return [positiveAxis, negativeAxis];
	}

	private createChildrenGridBoxes(
		parentId: string,
		worldTransform?: Transform,
		threshold?: number | ThresholdDimensions,
		includeChildOnly?: boolean,
		acc: Map<string, GridBox> = new Map<string, GridBox>()
	): Map<string, GridBox> {
		const parent = this.sceneObjects.get(parentId);
		if (!parent) {
			throw 'Grid: createChildrenGridBoxes(): No parent for children.';
		}
		const parentTransform = worldTransform
			? worldTransform
			: this.connectionManager.getWorldTransform(parent);

		const children = this.connectionManager.getChildren(parentId);
		for (const childId of children) {
			const sceneObject = this.sceneObjects.get(childId);
			if (sceneObject) {
				const { articleId, transform } = sceneObject;
				if (articleId == 'leg') {
					continue;
				}
				if (includeChildOnly && !this.connectionManager.isChildOnly(articleId)) {
					continue;
				}
				const childWorldTransform = this.connectionManager.getRelativeWorldTransform(
					transform,
					parentTransform
				);
				const childBox = this.createGridBox(
					articleId,
					childWorldTransform,
					this.getBoundsName(articleId),
					threshold
				);
				acc.set(childId, childBox);
				if (this.connectionManager.hasChildren(childId)) {
					this.createChildrenGridBoxes(
						childId,
						childWorldTransform,
						threshold,
						includeChildOnly,
						acc
					);
				}
			}
		}
		return acc;
	}

	private getBoundsPosition(articleId, bounds?: string): TransformPosition {
		const model = this.connectionManager.getModelData(articleId);
		if (!model) {
			throw 'Grid: getBoundsPosition(): No model.';
		}
		const boundsName = bounds ? bounds : model.bounds['extended'] ? 'extended' : 'default';
		return model.bounds[boundsName].position as TransformPosition;
	}

	public getBoundsName(articleId: string): string {
		const model = this.connectionManager.getModelData(articleId);
		if (!model) {
			throw 'Grid: getBoundsName(): No model.';
		}
		return model.bounds['extended'] ? 'extended' : 'default';
	}
}
